import { useCallback } from 'react';
import { Package as PackageIcon } from 'lucide-react';
import { useAppStore } from '@/store/store';
import { PackageRow } from './PackageRow';
import { EmptyState } from '@/components/shared/EmptyState';
import type { DimensionUnit, WeightUnit } from '@freight-cbm/shared-types';

export function PackageGrid() {
  const packages = useAppStore((s) => s.packages);
  const addPackage = useAppStore((s) => s.addPackage);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const text = e.clipboardData.getData('text/plain').trim();
      // Detect tabular paste: lines with tab or consistent comma separation
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 1) return;

      // Check if it looks like tabular data (at least 4 columns on first row)
      const firstFields = lines[0]!.split(/\t|,/);
      if (firstFields.length < 4) return; // Not tabular, let default paste happen

      e.preventDefault();

      for (const line of lines) {
        const fields = line.split(/\t|,/).map((f) => f.trim());
        const length = Number(fields[0]);
        const width = Number(fields[1]);
        const height = Number(fields[2]);
        const weight = Number(fields[3]);

        if (!Number.isFinite(length) || !Number.isFinite(width) || !Number.isFinite(height) || !Number.isFinite(weight)) {
          continue;
        }

        addPackage({
          length,
          width,
          height,
          weight,
          quantity: Number(fields[4]) || 1,
          dimUnit: (fields[5] as DimensionUnit) || 'cm',
          weightUnit: (fields[6] as WeightUnit) || 'kg',
        });
      }
    },
    [addPackage]
  );

  const thStyle: React.CSSProperties = {
    padding: 'var(--space-2) var(--space-1)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '2px solid var(--color-border)',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ overflowX: 'auto' }} onPaste={handlePaste}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'auto',
        }}
      >
        <thead>
          <tr>
            <th style={thStyle}>Length</th>
            <th style={thStyle}>Width</th>
            <th style={thStyle}>Height</th>
            <th style={thStyle}>Unit</th>
            <th style={thStyle}>Weight</th>
            <th style={thStyle}>Unit</th>
            <th style={thStyle}>Qty</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {packages.length === 0 ? (
            <tr>
              <td colSpan={8} style={{ padding: 0 }}>
                <EmptyState
                  icon={PackageIcon}
                  title="No packages yet"
                  description="Add the dimensions and weight of each package to calculate total CBM, chargeable weight, and shipping costs. You can also paste rows directly from a spreadsheet."
                  actions={[{ label: '+ Add first package', onClick: () => addPackage(), primary: true }]}
                  hint="Tip: paste tabular data (L, W, H, Weight, Qty) anywhere on the grid to import many rows at once."
                />
              </td>
            </tr>
          ) : (
            packages.map((pkg, i) => (
              <PackageRow key={pkg.id} pkg={pkg} index={i} />
            ))
          )}
        </tbody>
      </table>
      <button
        className="btn btn-primary"
        onClick={() => addPackage()}
        aria-label="Add new package row"
        style={{
          marginTop: 'var(--space-3)',
        }}
      >
        + Add Package
      </button>
    </div>
  );
}
